import { AdminApiError, type AdminClient, type SolveRtpResponse } from '@apx/admin-sdk';
import { askConfirm } from '../modal.js';

export interface TargetRtpPanelDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** 'per-bet' (sicbo/dragontiger affine solve — every bet lands at/under the
   *  target) vs 'overall' (greedyking — scales all multipliers to a target
   *  OVERALL RTP). Drives the panel copy only. Defaults to 'per-bet'. */
  solverKind?: 'per-bet' | 'overall';
  /** Called after a successful Apply so the host page can refresh its
   *  pending-approvals / live-RTP cards. */
  onApplied?: () => void | Promise<void>;
}

const DEFAULT_TARGET_PCT = 96;

// Mirror the Live-RTP card thresholds (sicbo/dragontiger multiplier pages):
// ≥100% is an error (pays over stake), ≥97% is a warning (thin house edge).
function rtpColor(v: number | null): string {
  if (v === null) return 'var(--muted)';
  if (v >= 1.0) return 'var(--error)';
  if (v >= 0.97) return 'var(--warn)';
  return 'var(--success)';
}

function pct(v: number | null): string {
  return v === null ? '—' : `${(v * 100).toFixed(1)}%`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

/**
 * Target-RTP solver panel (sicbo / dragontiger only). Lets an operator type a
 * single target RTP %, solve for a `multipliers` surface that lands every bet
 * type at/under that target, review the resulting per-bet RTP breakdown, and
 * Apply it through the SAME two-admin propose flow the manual editor uses.
 *
 * The server is the real cap gate (400 RTP_CAP_EXCEEDED for >100% unless
 * super_admin); the client-side >100% confirm is a UX nicety. Mounts above the
 * manual editor — pass `onApplied` so the host can refresh after Apply.
 */
export function renderTargetRtpPanel(deps: TargetRtpPanelDeps): HTMLElement {
  const isOverall = deps.solverKind === 'overall';
  // GreedyKING has no per-bet inverse — it scales all food multipliers to a
  // target OVERALL RTP; sicbo/dragontiger land every bet at/under the target.
  const blurb = isOverall
    ? 'Pick a target overall return-to-player and solve — this scales all food payout multipliers to hit it (GreedyKING has no per-bet solve). Review the breakdown, then Apply through two-admin approval.'
    : 'Pick a target return-to-player and solve for payout multipliers that land every bet type at or under it. Review the breakdown, then Apply to send the suggested multipliers through two-admin approval.';
  const note = isOverall
    ? 'Scales every food multiplier uniformly to the target overall RTP; the pool tier + integer multipliers may leave a point or two of gap.'
    : 'Applies to every bet type independently.';

  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `
    <div class="row" style="justify-content:space-between;align-items:center">
      <h2 style="margin:0">Target-RTP solver</h2>
    </div>
    <p class="muted" style="margin-top:6px">${blurb}</p>

    <div class="row" style="gap:12px;align-items:flex-end;flex-wrap:wrap;margin-top:8px">
      <label>Target RTP %
        <input id="tr-target" type="number" min="0" max="200" step="0.1" value="${DEFAULT_TARGET_PCT}" style="width:120px" />
      </label>
      <button id="tr-solve" class="primary" type="button">Solve</button>
      <button id="tr-apply" class="ghost" type="button" disabled>Apply (propose)</button>
      <span id="tr-msg" class="muted"></span>
    </div>
    <p class="muted" style="margin-top:6px;font-size:12px">${note}</p>

    <div id="tr-result" style="margin-top:12px"></div>
  `;

  const targetEl = card.querySelector<HTMLInputElement>('#tr-target')!;
  const solveBtn = card.querySelector<HTMLButtonElement>('#tr-solve')!;
  const applyBtn = card.querySelector<HTMLButtonElement>('#tr-apply')!;
  const msgEl = card.querySelector<HTMLElement>('#tr-msg')!;
  const resultEl = card.querySelector<HTMLElement>('#tr-result')!;

  // Seed the box from the saved target so the solver opens where the operator
  // left it; DEFAULT_TARGET_PCT only stands in when nothing is configured yet.
  void deps.client
    .getSurface(deps.integratorId, deps.gameId, 'weights')
    .then(({ value }) => {
      const saved = (value as { targetRtp?: unknown } | null)?.targetRtp;
      if (typeof saved === 'number' && saved > 0) targetEl.value = String(saved * 100);
    })
    .catch(() => {
      // Non-fatal: leave the default in place.
    });

  // Cached super_admin flag from /v1/me (UX nicety only — server gates >100%).
  let isSuperAdmin = false;
  void deps.client
    .me()
    .then((me) => {
      isSuperAdmin = me.isSuperAdmin;
    })
    .catch(() => {
      // Non-fatal: leave the client gate off and let the server reject >100%.
    });

  // Last successful solve — its multipliers surface is what Apply proposes.
  let lastSolved: SolveRtpResponse | null = null;

  function setMsg(text: string, kind: 'muted' | 'error' = 'muted'): void {
    msgEl.textContent = text;
    msgEl.className = kind;
  }

  function renderBreakdown(res: SolveRtpResponse): void {
    const rows = Object.entries(res.rtpBreakdown)
      .map(
        ([bet, rtp]) =>
          `<tr><td>${escapeHtml(bet)}</td>` +
          `<td style="text-align:right;color:${rtpColor(rtp)}">${pct(rtp)}</td></tr>`,
      )
      .join('');
    const warningsHtml =
      res.warnings.length > 0
        ? `<div class="card warn" style="margin-top:10px">
            <strong>Solver warnings</strong>
            <ul style="margin:6px 0 0;padding-left:18px">${res.warnings
              .map((w) => `<li class="muted">${escapeHtml(w)}</li>`)
              .join('')}</ul>
          </div>`
        : '';
    resultEl.innerHTML = `
      <table class="data-table">
        <thead><tr><th>Bet type</th><th style="text-align:right">Resulting RTP</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="2" class="muted">No bet types returned</td></tr>'}</tbody>
      </table>
      ${warningsHtml}`;
  }

  solveBtn.addEventListener('click', async () => {
    setMsg('');
    applyBtn.disabled = true;
    lastSolved = null;
    resultEl.innerHTML = '';

    const targetPctNum = Number(targetEl.value);
    if (!Number.isFinite(targetPctNum) || targetPctNum <= 0) {
      setMsg('Enter a target RTP greater than 0.', 'error');
      return;
    }

    // UX nicety: warn before sending a >100% target the user can apply.
    if (targetPctNum > 100) {
      if (isSuperAdmin) {
        const ok = await askConfirm(
          'RTP above 100% pays players more than they stake — confirm.',
          { danger: true },
        );
        if (!ok) return;
      }
      // Non-super-admins are allowed to TRY — the server returns the real 400.
    }

    solveBtn.disabled = true;
    setMsg('Solving…');
    try {
      const res = await deps.client.solveRtp(deps.integratorId, deps.gameId, {
        target: targetPctNum / 100,
      });
      lastSolved = res;
      renderBreakdown(res);
      applyBtn.disabled = false;
      setMsg('Solved — review the breakdown, then Apply to propose.');
    } catch (err) {
      lastSolved = null;
      applyBtn.disabled = true;
      if (err instanceof AdminApiError) {
        if (err.code === 'RTP_CAP_EXCEEDED') {
          setMsg(
            'Target exceeds 100% RTP — only a super admin can apply payouts above 100%.',
            'error',
          );
        } else {
          setMsg(`${err.code}: ${err.message}`, 'error');
        }
      } else {
        setMsg(err instanceof Error ? err.message : String(err), 'error');
      }
    } finally {
      solveBtn.disabled = false;
    }
  });

  applyBtn.addEventListener('click', async () => {
    if (!lastSolved) return;
    const multipliers = lastSolved.suggestedConfig.multipliers;
    if (!multipliers || typeof multipliers !== 'object') {
      setMsg('Solver returned no multipliers surface to apply.', 'error');
      return;
    }
    applyBtn.disabled = true;
    setMsg('Proposing…');
    try {
      // Send ONLY the (JSON-clean, bps-integer) multipliers object — the rest of
      // suggestedConfig carries server-stringified bigint fields we must not push.
      const proposed = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        multipliers as never,
      );
      setMsg(
        `Pending approval ${proposed.approval.id}. A different admin must approve.`,
      );
      await deps.onApplied?.();
    } catch (err) {
      applyBtn.disabled = false;
      if (err instanceof AdminApiError) {
        if (err.code === 'RTP_CAP_EXCEEDED') {
          setMsg(
            'Target exceeds 100% RTP — only a super admin can apply payouts above 100%.',
            'error',
          );
        } else {
          setMsg(`${err.code}: ${err.message}`, 'error');
        }
      } else {
        setMsg(err instanceof Error ? err.message : String(err), 'error');
      }
    }
  });

  return card;
}
